// IMPORTAÇÃO DAS BIBLIOTECAS----------------------------------------------------------------------
import { useState, useEffect, useMemo } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import styles from "./newNote.module.css";
import { useTranslation } from 'react-i18next';
// ------------------------------------------------------------------------------------------------



// IMPORTAÇÃO DOS COMPONENTES ---------------------------------------------------------------------
import {
  MdSave, MdExpandMore, MdExpandLess, MdClose,
  MdFormatBold, MdFormatItalic,
  MdFormatColorText, MdSettings,
  MdPalette, MdOutlineDragIndicator, MdHelpOutline
} from "react-icons/md";
import { IoAddCircle, IoClose } from "react-icons/io5";
import {
  BsListUl, BsImage, BsFileEarmarkPdf, BsMicFill, BsCamera, BsStars
} from "react-icons/bs";
import { useAppContext } from "../../../AppContext.js"; // hook para acessar o estado global do App
import InfoAnimationErro from '../../../components/infoAnimation/infoAnimationErro.js' // componente para exibir mensagens de erro
// ------------------------------------------------------------------------------------------------



const NewNote = ({ onConfirm, onCancel, noteToEdit }) => {

  // FUNÇÕES PARA INTERAGIR COM O LOCALSTORAGE-----------------------------------------------------
  const getFromStorage = (key) => !noteToEdit ? localStorage.getItem(key) : null; // Recupera valor do localStorage
  const saveToStorage = (key, value) => { if (!noteToEdit) localStorage.setItem(key, value); }; // Salva valor no localStorage
  const removeFromStorage = (key) => { if (!noteToEdit) localStorage.removeItem(key); }; // Remove valor do localStorage



  // ESTADOS E VARIÁVEIS---------------------------------------------------------------------------
  const [title, setTitle] = useState(noteToEdit ? noteToEdit.title : (getFromStorage('newNoteTitle') || '')); // Título da nota
  const [notes, setNotes] = useState(noteToEdit ? noteToEdit.notes : (getFromStorage('newNoteContent') || '')); // Conteúdo da nota
  const [listContent, setListContent] = useState(() => {  // lista de itens da nota
    try {
      if (noteToEdit && noteToEdit.listContent) return noteToEdit.listContent;
      const saved = getFromStorage('newListContent');
      if (saved) return JSON.parse(saved);
      return { item: [], status: [] };
    } catch {
      return { item: [], status: [] };
    }
  });
  const [colorFont, setColorFont] = useState(noteToEdit ? noteToEdit.colorFont : (getFromStorage('newNoteColorFont') || "colorBlack")); // Cor da fonte da nota
  const [background, setBackground] = useState(noteToEdit ? noteToEdit.background : (getFromStorage('newNoteBackground') || "bgYellow")); // Cor de fundo da nota
  const [fontWeight, setFontWeight] = useState(noteToEdit ? noteToEdit.fontWeight : (getFromStorage('newNoteFontWeight') || "")); // Negrito da fonte
  const [italicFont, setItalicFont] = useState(noteToEdit ? noteToEdit.italicFont : (getFromStorage('newNoteItalicFont') || "")); // Itálico da fonte
  const [font, setFont] = useState(noteToEdit ? noteToEdit.font : (getFromStorage('newNoteFont') || "estiloTextoA")); // Fonte da nota
  const [focus, setFocus] = useState('title'); // Controla o foco para inserção de emojis e itens da lista
  const [priority, setPriority] = useState(noteToEdit ? noteToEdit.priority : false); // Prioridade da nota
  const [addList, setAddList] = useState(() => { // Adiciona lista à nota
    try {
      if (noteToEdit && noteToEdit.listContent) return noteToEdit.listContent.item.length > 0; // Se a nota tiver lista, retorna true
      const saved = getFromStorage('newListContent'); // Recupera lista do localStorage
      if (saved) return JSON.parse(saved).item.length > 0; // Se a lista existir no localStorage, retorna true
      return false; // Caso contrário, retorna false
    } catch {
      return false; // Caso ocorra um erro, retorna false
    }
  });
  const [focusIndex, setFocusIndex] = useState(-1); // index do item da lista que recebeu foco
  const [activeListItemIndex, setActiveListItemIndex] = useState(-1); // index do item da lista ativo para emojis
  const [draggedItemIndex, setDraggedItemIndex] = useState(null); // index do item sendo arrastado
  const [dragOverIndex, setDragOverIndex] = useState(null); // index do item sobre o qual o arrastado está
  const [loading, setLoading] = useState(false); // Estado de carregamento para salvar a nota
  const [showEmojis, setShowEmojis] = useState(false); // Painel de emojis flutuante
  const [expandEmojis, setExpandEmojis] = useState(false); // Expandir emojis
  const [keyboardOffset, setKeyboardOffset] = useState(0); // Offset do teclado para mobile
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false); // Indica se o teclado virtual está aberto
  // const [error, setError] = useState(''); // Estado de erro ao salvar nota
  const [showAdvanced, setShowAdvanced] = useState(false); // exibe configurações avançadas
  const { t } = useTranslation(); // Hook de tradução
  const { isApp } = useAppContext(); // define se está sendo acessado pelo app ou pelo site

  // Estados e handlers para animação de erro
  const [showErro, setShowErro] = useState(false);
  const [showErroMsg, setShowErroMsg] = useState('');
  const [showErroDuration, setShowErroDuration] = useState(0);

  // Estados para o tutorial premium
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [showListHelp, setShowListHelp] = useState(false); // exibe tooltip de atalhos da lista



  // FUNÇÕES---------------------------------------------------------------------------------------
  // hook para inicialização
  useEffect(() => {
    sessionStorage.setItem('showEmojis', 'true'); // habilita novamente o painel emoji caso o usuário tenha fechado na ultima sessão
  }, []);

  // Gerencia efeitos colaterais do modal (scroll lock, fechar com ESC).
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleEsc = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", handleEsc);

    // Função de limpeza
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEsc);
    };
  }, [onCancel]);

  // Função para salvar a nota
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    //setError('');
    try {
      await onConfirm(title, notes, addList ? listContent : null, colorFont, background, fontWeight, italicFont, font, priority); // supondo que onConfirm retorna uma Promise
      setTitle(''); setNotes(''); setListContent({ item: [], status: [] }); setLoading(false); onCancel();
      // Limpa os dados salvos no localStorage após salvar a nota
      removeFromStorage('newNoteTitle');
      removeFromStorage('newNoteContent');
      removeFromStorage('newListContent');
      // removeFromStorage('newNoteColorFont');
      // removeFromStorage('newNoteBackground');
      // removeFromStorage('newNoteFontWeight');
      // removeFromStorage('newNoteItalicFont');
      // removeFromStorage('newNoteFont');
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setShowErroMsg(t('User deactivated. Subscribe to a plan to continue using the app!'));
      } else {
        setShowErroMsg(t('Error saving note. Try again.'));
      }
      setShowErroDuration(2500); setShowErro(true); setLoading(false);
    }
  };

  // Função para salvar e criar nova nota
  async function createNewNote() {
    setLoading(true);
    //setError('');
    try {
      await onConfirm(title, notes, addList ? listContent : null, colorFont, background, fontWeight, italicFont, font, priority); // supondo que onConfirm retorna uma Promise
      setTitle(''); setNotes(''); setListContent({ item: [], status: [] })
      // Limpa os dados salvos no localStorage após salvar a nota
      removeFromStorage('newNoteTitle');
      removeFromStorage('newNoteContent');
      removeFromStorage('newListContent');
      // removeFromStorage('newNoteColorFont');
      // removeFromStorage('newNoteBackground');
      // removeFromStorage('newNoteFontWeight');
      // removeFromStorage('newNoteItalicFont');
      // removeFromStorage('newNoteFont');
      setLoading(false);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setShowErroMsg(t('User deactivated. Subscribe to a plan to continue using the app!'));
      } else {
        setShowErroMsg(t('Error saving note. Try again.'));
      }
      setShowErroDuration(2500); setShowErro(true); setLoading(false);
    }
  }

  // Funções para manipulação da lista
  const addListItem = (index = -1, position = 'after') => {
    // só permite inserir até 500 itens
    if (listContent.item.length >= 500) return;
    setListContent(prev => {
      const newItems = [...prev.item];
      const newStatus = [...prev.status];

      if (index === -1) {
        newItems.push('');
        newStatus.push(false);
      } else {
        const insertIndex = position === 'before' ? index : index + 1;
        newItems.splice(insertIndex, 0, '');
        newStatus.splice(insertIndex, 0, false);
      }

      return { item: newItems, status: newStatus };
    });

    setFocusIndex(index === -1 ? listContent.item.length : (position === 'before' ? index : index + 1));

    if (index === -1) {
      setTimeout(() => {
        const noteArea = document.getElementById('noteAreaScroll');
        if (noteArea) {
          noteArea.scrollTop = noteArea.scrollHeight;
        }
      }, 50);
    }

  };

  // função para adicionar itens da lista quando o usuário pressionar Enter
  const handleListKeyDown = (e, index) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (e.ctrlKey) {
        addListItem(index, 'before');
      } else {
        addListItem(index, 'after');
      }
    }
  };

  // função para alterar o status dos itens da lista
  const handleListItemChange = (e, index) => {
    const newStatus = [...listContent.status];
    newStatus[index] = e.target.checked;
    setListContent({ ...listContent, status: newStatus });
  };

  // função para alterar o conteúdo dos itens da lista
  const handleListItemInputChange = (e, index) => {
    const newItems = [...listContent.item];
    newItems[index] = e.target.value;
    setListContent({ ...listContent, item: newItems });
  };

  // função para remover itens da lista
  const handleRemoveListItem = (index) => {
    const newItems = listContent.item.filter((_, i) => i !== index);
    const newStatus = listContent.status.filter((_, i) => i !== index);
    setListContent({ item: newItems, status: newStatus });
  };

  // funções para criar marcadores automáticos
  function handleKeyUp(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      const txa = document.getElementById('txarea');
      const locCursor = txa.selectionStart;
      const textBeforeCursor = txa.value.slice(0, locCursor).trim();

      // Só insere marcador se a frase anterior terminar com ":" ou ";"
      if (textBeforeCursor.endsWith(":") || textBeforeCursor.endsWith(";")) {
        txa.value = txa.value.slice(0, locCursor) + "   ●  " + txa.value.slice(locCursor);
        setTimeout(() => { txa.selectionStart = txa.selectionEnd = locCursor + 6; }, 0);
      }
    }
  }



  // salva o conteudo da lista no localStorage
  useEffect(() => {
    saveToStorage('newListContent', JSON.stringify(listContent));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listContent, noteToEdit]);

  // Efeito para focar no novo item da lista
  useEffect(() => {
    if (focusIndex !== -1) {
      const inputs = document.getElementsByClassName(styles.listItemInput);
      if (inputs[focusIndex]) {
        inputs[focusIndex].focus();
      }
      setFocusIndex(-1);
    }
  }, [listContent.item, focusIndex]);

  // funções para reordenar itens da lista (Drag and Drop)
  const handleDragStart = (e, index) => {
    setDraggedItemIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Define um efeito visual no elemento arrastado
    setTimeout(() => {
      e.target.classList.add(styles.dragging);
    }, 0);
  };

  // ao passar o mouse sobre um item, define o index
  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  // solta o item na nova posição
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedItemIndex === null || draggedItemIndex === targetIndex) {
      setDragOverIndex(null);
      return;
    }

    const newItems = [...listContent.item];
    const newStatus = [...listContent.status];

    // Remove o item da posição original
    const [movedItem] = newItems.splice(draggedItemIndex, 1);
    const [movedStatus] = newStatus.splice(draggedItemIndex, 1);

    // Insere na nova posição
    newItems.splice(targetIndex, 0, movedItem);
    newStatus.splice(targetIndex, 0, movedStatus);

    setListContent({ item: newItems, status: newStatus });
    setDraggedItemIndex(null);
    setDragOverIndex(null);
  };

  // finaliza o drag and drop
  const handleDragEnd = (e) => {
    setDraggedItemIndex(null);
    setDragOverIndex(null);
    e.target.classList.remove(styles.dragging);
  };

  // Função auxiliar para inserir emoji na posição do cursor
  const insertEmojiAtCursor = (emoji) => {
    if (focus === 'title') {
      const inputElement = document.querySelector(`input[aria-label="${t('Title of the note')}"]`);
      if (inputElement) {
        const cursorPos = inputElement.selectionStart;
        const newTitle = title.slice(0, cursorPos) + emoji + title.slice(cursorPos);

        // Verifica se ultrapassa o limite de 40 caracteres
        if (newTitle.length > 40) return;

        setTitle(newTitle);

        // Salva o título atualizado no localStorage para evitar perda de dados
        saveToStorage('newNoteTitle', newTitle);

        // Reposiciona o cursor após o emoji inserido
        setTimeout(() => { inputElement.selectionStart = inputElement.selectionEnd = cursorPos + emoji.length; }, 0);
      }
    } else if (focus === 'list') {
      if (activeListItemIndex === -1) return;
      const newItems = [...listContent.item];
      const currentItem = newItems[activeListItemIndex];

      const inputs = document.getElementsByClassName(styles.listItemInput);
      const inputElement = inputs[activeListItemIndex];

      if (inputElement) {
        const cursorPos = inputElement.selectionStart;
        const newItemContent = currentItem.slice(0, cursorPos) + emoji + currentItem.slice(cursorPos);

        newItems[activeListItemIndex] = newItemContent;
        setListContent({ ...listContent, item: newItems });

        // Reposiciona o cursor após o emoji inserido
        setTimeout(() => { inputElement.selectionStart = inputElement.selectionEnd = cursorPos + emoji.length; }, 0);
      }
    } else {
      const textareaElement = document.getElementById('txarea');
      if (textareaElement) {
        const cursorPos = textareaElement.selectionStart;
        const newNotes = notes.slice(0, cursorPos) + emoji + notes.slice(cursorPos);

        // Verifica se ultrapassa o limite de 5000 caracteres
        if (newNotes.length > 5000) return;

        setNotes(newNotes);

        // Salva o conteúdo atualizado no localStorage para evitar perda de dados
        saveToStorage('newNoteContent', newNotes);

        // Reposiciona o cursor após o emoji inserido
        setTimeout(() => { textareaElement.selectionStart = textareaElement.selectionEnd = cursorPos + emoji.length; }, 0);
      }
    }
  };

  // emojis para o painel de simbolos, organizados por categorias para facilitar a busca do usuário
  const emojisByCategory = useMemo(() => ({
    [t('Emotions and expressions')]: ["😀", "😁", "😂", "🤣", "😃", "😄", "😆", "😊", "😇", "🙂", "🙃", "😍", "🥰", "😘", "😗", "😙", "😚", "🤩", "🥳", "😎", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "😱", "😨", "😰", "😥", "😓", "😪", "😴", "🥱", "😵", "🤯", "😳", "😶", "😐", "😑", "🙄", "😒", "😬", "👍", "👎", "👏", "🙌", "🙏", "🤝", "✌️", "🤟", "👌", "🤌", "🤏", "✊", "👊", "🤛", "🤜", "🤚", "🖐️", "✋", "🖖", "🤘", "🤙", "👋", "🤲", "👐", "🤗", "🤭", "🤫", "🤔", "🤨", "🤐", "🤥", "🤢", "🤮", "🤧", "😷", "🤒", "🤕", "👻", "💀", "☠️", "👽", "🤖", "🎃", "😈", "👿", "👹", "👺", "😺", "😸", "😹", "😻", "😼", "😽", "🙀", "😿", "😾"],
    [t('Ideas and highlights')]: ["💡", "⭐", "✨", "🔥", "🌟", "🎯", "💎", "🏆", "🎖️", "📍", "👑", "🌠", "💫", "⚡", "🔆"],
    [t('Status and tasks')]: ["✅", "❌", "⚠️", "❗", "❓", "⏳", "⌛", "🔒", "🔓", "🔑", "⚙️", "🔄", "✔️", "📋", "📝"],
    [t('Financial')]: ["💰", "💵", "💴", "💶", "💷", "💳", "💸", "🏦", "📈", "📉", "💹", "🧾", "💱", "🤑", "💼", "🛒"],
    [t('Organization and documents')]: ["📌", "📎", "📅", "📍", "📝", "📖", "📚", "📂", "📁", "📤", "📥", "📋", "📄", "📃", "📑", "🗂️", "🗃️"],
    [t('Time and calendar')]: ["⏰", "🕒", "📆", "🗓️", "⏱️", "⌚", "🕰️", "📅", "🗒️", "⏳", "⌛"],
    [t('Communication')]: ["📞", "☎️", "✉️", "📧", "💬", "🔔", "🔕", "📱", "💭", "🗨️", "💌", "📨", "📬"],
    [t('Technology and devices')]: ["💻", "🖥️", "🖨️", "⌨️", "🖱️", "📱", "📲", "📡", "📺", "🎧", "🎤", "📷", "🎥", "💾"],
  }), [t]);

  // Listener para ajustar o painel de emojis quando o teclado virtual abrir (iOS e Android)
  useEffect(() => {
    const handleViewportChange = () => {
      if (window.visualViewport) {
        // Calcula a diferença entre a viewport de layout e a viewport visual
        // Isso resolve o problema do teclado cobrindo o painel fixo.
        const offset = window.innerHeight - window.visualViewport.height - window.visualViewport.offsetTop;
        setKeyboardOffset(Math.max(0, offset));
        setIsKeyboardVisible(window.visualViewport.height < window.screen.height * 0.75);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
      handleViewportChange(); // Chamada inicial

      return () => {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      };
    }
  }, []);

  //------------------------------------------------------------------------------------------------



  // HTML DO COMPONENTE----------------------------------------------------------------------------
  return (
    // <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
    <div className={styles.overlay}>
      <form className={`${styles.card} ${isApp ? styles.cardApp : ''}`} onSubmit={handleSubmit}>

        {/* Barra de acento superior */}
        <div className={styles.accentBar} />

        {/* Cabeçalho */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.headerDot} />
            <h2 className={styles.heading}>{noteToEdit ? t('Edit Note') : t('New Note')}</h2>
          </div>
          <div className={styles.headerRight}>
            <button
              type="button"
              className={styles.tutorialBtn}
              onClick={() => { setShowTutorial(true); setTutorialStep(0); }}
              title={t('Tutorial')}
              aria-label={t('Tutorial')}
            >
              <BsStars size={16} />
            </button>
            <button
              type="button"
              className={`${styles.close} ${isApp ? styles.closeApp : ''}`}
              onClick={onCancel}
              aria-label={t("Close")}
            >
              <MdClose size={18} />
            </button>
          </div>
        </div>

        {/* ÁREA DE ESCRITA DA NOTA */}
        <div id="noteAreaScroll" className={`${styles.noteArea} ${styles[background]} ${styles[colorFont]}`}>
          {/* Campo título */}
          <div className={styles.fieldWrap}>
            <input
              className={`${styles.input} ${styles[colorFont]} ${styles[font]}`}
              type="text"
              placeholder={t("Title")}
              maxLength={40}
              value={title}
              onChange={e => {
                setTitle(e.target.value);
                saveToStorage('newNoteTitle', e.target.value);
              }}
              // required
              aria-label={t('Title of the note')}
              onClick={() => { setFocus('title'); setShowEmojis(true); }}
              onFocus={() => { setFocus('title'); setShowEmojis(true); }}
              onBlur={() => { setShowEmojis(false); }}
              style={{ fontStyle: italicFont }}
            />
            <span className={styles.charCount}>{title.length}/40</span>
          </div>

          {/* Campo texto */}
          <div id="fieldWrapTextArea" className={styles.fieldWrap}>
            <TextareaAutosize
              id="txarea"
              className={`${styles.textarea} ${styles[colorFont]} ${styles[font]}`}
              onKeyUp={handleKeyUp}
              placeholder={t("Describe your note")}
              value={notes}
              onChange={e => {
                setNotes(e.target.value);
                saveToStorage('newNoteContent', e.target.value);
              }}
              // required
              minRows={1}
              maxLength={5000}
              aria-label={t('Content of the note')}
              onClick={() => { setFocus('notes'); setShowEmojis(true); }}
              onFocus={() => { setFocus('notes'); setShowEmojis(true); }}
              onBlur={() => { setShowEmojis(false); }}
              style={{ fontWeight: fontWeight, fontStyle: italicFont }}
            />
            <span className={`${styles.charCount} ${styles.charCountBottom}`}>{notes.length}/5000</span>
          </div>

          {/* campo lista */}
          {addList && (
            <div className={`${styles.fieldWrap} ${styles.listContainer}`}>
              {listContent.item.map((item, index) => (
                <div
                  key={index}
                  className={`${styles.listItem} ${draggedItemIndex === index ? styles.dragging : ''} ${dragOverIndex === index ? styles.dragOver : ''}`}
                  draggable="true"
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                >

                  {/* icone para arrastar e reordenar itens da lista */}
                  <span className={styles.listItemIcon} onMouseDown={(e) => {
                    // Garante que o drag só comece pelo ícone em alguns browsers, 
                    // embora o draggable esteja no listItem todo.
                  }}>
                    <MdOutlineDragIndicator size={16} />
                  </span>

                  {/* checkbox personalizado */}
                  <label className={styles.checkboxContainer}>
                    <input
                      type="checkbox"
                      checked={listContent.status[index]}
                      onChange={(e) => handleListItemChange(e, index)}
                      aria-label={t('Mark item as complete')}
                    />
                    <span className={styles.checkmark}></span>
                  </label>
                  {/* area de texto do item da lista */}
                  <TextareaAutosize
                    maxLength={500}
                    minRows={1}
                    className={`${styles.listItemInput} ${styles[colorFont]} ${styles[font]}`}
                    style={{ fontStyle: italicFont, textDecoration: listContent.status[index] ? 'line-through' : 'none', opacity: listContent.status[index] ? 0.6 : 1, resize: 'none', overflow: 'hidden' }}
                    value={item}
                    onChange={(e) => handleListItemInputChange(e, index)}
                    onKeyDown={(e) => handleListKeyDown(e, index)}
                    enterKeyHint="enter" // correção de bug em dispositivos moveis
                    placeholder={t('Type your item')}
                    aria-label={t('Item content')}
                    onFocus={(e) => {
                      setFocus('list'); setActiveListItemIndex(index); setShowEmojis(true);
                    }}
                    onClick={(e) => {
                      setFocus('list'); setActiveListItemIndex(index); setShowEmojis(true);
                    }}
                    onBlur={() => { setShowEmojis(false); }}
                    {...(isApp ? { onDoubleClick: () => addListItem(index, 'before') } : {})}
                  />

                  <button
                    type="button"
                    className={styles.listItemRemoveBtn}
                    onClick={() => { handleRemoveListItem(index); if (listContent.item.length === 1) { setAddList(false); } }}
                    aria-label={t('Remove item')}
                  >
                    <IoClose size={18} />
                  </button>
                </div>
              ))}
              <div className={styles.addListContainer}>
                <button type="button" className={styles.addListBtn} onClick={() => addListItem()} aria-label={t('Add item to list')}>
                  <IoAddCircle size={18} />
                  <span>{t('Add item')}</span>
                </button>
                <button type="button" className={styles.listHelpBtn} aria-label={t('Help')} onClick={() => setShowListHelp(!showListHelp)}>
                  <MdHelpOutline size={16} />
                </button>
                {showListHelp && (
                  <div className={styles.listHelpTooltip}>
                    <button type="button" className={styles.listHelpClose} onClick={() => setShowListHelp(false)} aria-label={t('Close')}>
                      <MdClose size={14} />
                    </button>
                    {isApp
                      ? t('Shortcuts.app')
                      : t('Shortcuts.web')
                    }
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Área para anexos e configurações avançadas */}
        {/* Botão configurações avançadas */}
        <button
          type="button"
          className={`${styles.advancedToggle} ${showAdvanced ? styles.advancedToggleActive : ''}`}
          onClick={() => setShowAdvanced(!showAdvanced)}
          aria-expanded={showAdvanced}
          aria-label={t('Note styles')}
        >
          <MdSettings size={14} className={styles.advancedIcon} />
          <span>{t('Note styles')}</span>
          <span className={styles.advancedChevron}>
            {showAdvanced ? <MdExpandLess size={15} /> : <MdExpandMore size={15} />}
          </span>
        </button>

        {/* Área para anexos */}
        <div className={styles.attachContainer}>
          {/* Toolbar de anexos */}
          <div className={styles.attachToolbar}>
            <span className={styles.attachLabel}>{t('Add')}</span>
            <div className={styles.attachDivider} />
            <div className={styles.attachActions}>

              <button type="button" className={`${styles.attachBtn} ${styles.attachList}`} aria-label={t('Add list')} title={t('Add list')} onClick={() => {
                if (addList && listContent.item.length === 0) {
                  setAddList(false);
                } else {
                  setAddList(true);

                  if (!addList && listContent.item.length === 0) {
                    addListItem();
                  }
                }
              }}>
                <span className={styles.attachIcon}><BsListUl size={16} /></span>
                <span className={styles.attachText}>{t('List')}</span>
              </button>

              <div className={styles.emBreveContainer}>
                <div className={styles.emBreveOverlay}>
                  <BsStars size={18} className={styles.emBreveIcon} />
                  <span className={styles.emBreveText}>{t('Coming soon')}</span>
                </div>
                <div className={styles.emBreveItems}>
                  <button type="button" className={`${styles.attachBtn} ${styles.attachCamera}`} aria-label={t('Take photo')} title={t('Take photo')}>
                    <span className={styles.attachIcon}><BsCamera size={16} /></span>
                    <span className={styles.attachText}>{t('Camera')}</span>
                  </button>

                  <button type="button" className={`${styles.attachBtn} ${styles.attachGallery}`} aria-label={t('Add image from file')} title={t('Add image from file')}>
                    <span className={styles.attachIcon}><BsImage size={16} /></span>
                    <span className={styles.attachText}>{t('Gallery')}</span>
                  </button>

                  <button type="button" className={`${styles.attachBtn} ${styles.attachPdf}`} aria-label={t('Add PDF')} title={t('Add PDF')}>
                    <span className={styles.attachIcon}><BsFileEarmarkPdf size={16} /></span>
                    <span className={styles.attachText}>PDF</span>
                  </button>

                  <button type="button" className={`${styles.attachBtn} ${styles.attachAudio}`} aria-label={t('Record audio')} title={t('Record audio')}>
                    <span className={styles.attachIcon}><BsMicFill size={16} /></span>
                    <span className={styles.attachText}>{t('Audio')}</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Footer: prioridade + botões */}
        <div className={styles.footer} style={{ paddingBottom: isApp ? '60px' : '20px' }}>
          <label className={styles.priorityToggle} aria-label={t('Priority')}>
            <input
              type="checkbox"
              className={styles.priorityInput}
              checked={priority}
              onChange={e => setPriority(e.target.checked)}
            />
            <span className={styles.priorityTrack}>
              <span className={styles.priorityThumb} />
            </span>
            <span className={styles.priorityLabel}>{t('Priority')}</span>
          </label>

          <div className={styles.buttonGroup}>
            <button
              className={styles.cancelButton}
              type="button"
              onClick={() => {
                onCancel();
                removeFromStorage('newNoteTitle');
                removeFromStorage('newNoteContent');
                removeFromStorage('newListContent');
                // removeFromStorage('newNoteColorFont');
                // removeFromStorage('newNoteBackground');
                // removeFromStorage('newNoteFontWeight');
                // removeFromStorage('newNoteItalicFont');
                // removeFromStorage('newNoteFont');
              }}
              aria-label={t('Cancel')}
            >
              {t('Cancel')}
            </button>

            <button
              id="new-note-button"
              className={styles.buttonSN}
              type="button"
              disabled={(title.trim() === '' && notes.trim() === '' && listContent.item.length === 0) || loading}
              onClick={createNewNote}
              aria-label={t('save and new')}
            >
              <IoAddCircle size={15} />
              {t('save and new')}
            </button>

            <button
              className={styles.button}
              type="submit"
              disabled={(title.trim() === '' && notes.trim() === '' && listContent.item.length === 0) || loading}
              aria-label={t('Save')}
            >
              {loading ? <span className={styles.loader} /> : <MdSave size={15} />}
              {loading ? '' : t('Save')}
            </button>
          </div>
        </div>

        {/* painel de emojis flutuante */}
        {isApp && showEmojis && isKeyboardVisible && sessionStorage.getItem('showEmojis') !== 'false' && (
          <div
            className={`${styles.floatingEmojiPanel} ${styles.visible}`}
            style={{ bottom: `${keyboardOffset}px` }}
          >
            <div className={styles.emojiHeader}>
              <h5>{t('Favorite emojis')}</h5>
              <div className={styles.emojiControls}>
                <button type="button" className={styles.iconButton} onPointerDown={e => e.preventDefault()} onClick={() => setExpandEmojis(!expandEmojis)} title={expandEmojis ? t('Show less') : t('Show more')}>
                  {expandEmojis ? <MdExpandLess /> : <MdExpandMore />}
                </button>
                <button type="button" className={styles.iconButton} onPointerDown={e => e.preventDefault()} onClick={() => { setShowEmojis(false); sessionStorage.setItem('showEmojis', 'false'); setExpandEmojis(false); }} title={t('Close')}>
                  <MdClose />
                </button>
              </div>
            </div>

            <div className={styles.favoritesRow}>
              {(() => {
                if (!localStorage.getItem('emojiUsage')) {
                  const allEmojis = Object.values(emojisByCategory).flat();
                  const randomEmojis = allEmojis.sort(() => 0.5 - Math.random()).slice(0, 10);
                  localStorage.setItem('emojiUsage', JSON.stringify(randomEmojis.map(e => ({ emoji: e, count: 0, lastUsed: Date.now() }))));
                }
                return JSON.parse(localStorage.getItem('emojiUsage'))?.map((item, index) => (
                  <button key={index} type="button" onPointerDown={e => e.preventDefault()} onClick={e => { e.preventDefault(); insertEmojiAtCursor(item.emoji); }}>
                    {item.emoji}
                  </button>
                ));
              })()}
            </div>

            <div className={`${styles.allEmojisContainer} ${expandEmojis ? styles.expanded : ''}`}>
              {Object.entries(emojisByCategory).map(([category, emojis]) => (
                <div key={category} className={styles.emojiCategory}>
                  <h6>{t(category)}</h6>
                  <div className={styles.emojiGrid}>
                    {emojis.map((emoji, index) => (
                      <button
                        key={`${category}-${index}`}
                        type="button"
                        onPointerDown={e => e.preventDefault()}
                        onClick={e => {
                          e.preventDefault();
                          let emojiUsage = JSON.parse(localStorage.getItem('emojiUsage')) || [];
                          const now = Date.now();
                          const existingIndex = emojiUsage.findIndex(e => e.emoji === emoji);
                          if (existingIndex !== -1) {
                            emojiUsage[existingIndex].count += 1;
                            emojiUsage[existingIndex].lastUsed = now;
                          } else {
                            if (emojiUsage.length >= 10) {
                              emojiUsage.sort((a, b) => b.count === a.count ? b.lastUsed - a.lastUsed : b.count - a.count);
                              emojiUsage.pop();
                            }
                            emojiUsage.push({ emoji, count: 1, lastUsed: now });
                          }
                          emojiUsage.sort((a, b) => b.count === a.count ? b.lastUsed - a.lastUsed : b.count - a.count);
                          emojiUsage = emojiUsage.slice(0, 10);
                          localStorage.setItem('emojiUsage', JSON.stringify(emojiUsage));
                          insertEmojiAtCursor(emoji);
                        }}>
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* mensagem de erro ao realizar ações */}
        {showErro && (
          <InfoAnimationErro
            message={showErroMsg}
            duration={showErroDuration}
            onClose={() => setShowErro(false)}
          />
        )}

        {/* painel de erro */}
        {/* {error && (
          <div className={styles.error}>{error}</div>
        )} */}

        {/* Painel de configurações avançadas (Modal Flutuante) */}
        <div className={`${styles.advancedPanel} ${showAdvanced ? styles.advancedPanelOpen : ''}`}>
          <div className={styles.advancedOverlay} onClick={() => setShowAdvanced(false)} />
          <div className={styles.advancedContent}>
            <div className={styles.advancedHeader}>
              <h3>{t('Note styles')}</h3>
              <button type="button" onClick={() => setShowAdvanced(false)} className={styles.advancedClose}>
                <MdClose size={20} />
              </button>
            </div>

            {/* Cor de fundo da nota */}
            <div className={styles.advancedGroup}>
              <span className={styles.advancedGroupLabel}><MdPalette size={14} /> {t('Note colors')}</span>
              <div className={styles.colorRow}>
                <button type="button" className={`${styles.colorDotBg} ${styles.bgWrite} ${background === "bgWrite" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgWrite"); saveToStorage('newNoteBackground', 'bgWrite'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgBlue} ${background === "bgBlue" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgBlue"); saveToStorage('newNoteBackground', 'bgBlue'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgGreen} ${background === "bgGreen" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgGreen"); saveToStorage('newNoteBackground', 'bgGreen'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgPink} ${background === "bgPink" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgPink"); saveToStorage('newNoteBackground', 'bgPink'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgGray} ${background === "bgGray" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgGray"); saveToStorage('newNoteBackground', 'bgGray'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgPurple} ${background === "bgPurple" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgPurple"); saveToStorage('newNoteBackground', 'bgPurple'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgRed} ${background === "bgRed" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgRed"); saveToStorage('newNoteBackground', 'bgRed'); }} />
                <button type="button" className={`${styles.colorDotBg} ${styles.bgYellow} ${background === "bgYellow" ? styles.colorDotBgSelected : ""}`} onClick={() => { setBackground("bgYellow"); saveToStorage('newNoteBackground', 'bgYellow'); }} />
              </div>
            </div>

            {/* Cor do texto */}
            <div className={styles.advancedGroup}>
              <span className={styles.advancedGroupLabel}><MdFormatColorText size={14} /> {t('Text colors')}</span>
              <div className={styles.colorRow}>
                <button type="button" className={`${styles.colorDot} ${styles.colorBlack} ${colorFont === "colorBlack" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorBlack"); saveToStorage('newNoteColorFont', 'colorBlack'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorRed} ${colorFont === "colorRed" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorRed"); saveToStorage('newNoteColorFont', 'colorRed'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorOrange} ${colorFont === "colorOrange" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorOrange"); saveToStorage('newNoteColorFont', 'colorOrange'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorGreen} ${colorFont === "colorGreen" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorGreen"); saveToStorage('newNoteColorFont', 'colorGreen'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorBlue} ${colorFont === "colorBlue" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorBlue"); saveToStorage('newNoteColorFont', 'colorBlue'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorPurple} ${colorFont === "colorPurple" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorPurple"); saveToStorage('newNoteColorFont', 'colorPurple'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorPink} ${colorFont === "colorPink" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorPink"); saveToStorage('newNoteColorFont', 'colorPink'); }} />
                <button type="button" className={`${styles.colorDot} ${styles.colorYellow} ${colorFont === "colorYellow" ? styles.colorDotSelected : ""}`} onClick={() => { setColorFont("colorYellow"); saveToStorage('newNoteColorFont', 'colorYellow'); }} />
              </div>
            </div>

            {/* Formatação do texto */}
            <div className={styles.advancedGroup}>
              <span className={styles.advancedGroupLabel}>{t('Text format')}</span>
              <div className={styles.advancedRow}>
                <button type="button" className={`${styles.formatBtn} ${fontWeight === '500' ? styles.formatBtnActive : ''}`} aria-label={t('Bold')} title={t('Bold')} onClick={() => { fontWeight === '500' ? setFontWeight('') : setFontWeight('500'); saveToStorage('newNoteFontWeight', fontWeight === '500' ? '' : '500'); }}><MdFormatBold size={17} /></button>
                <button type="button" className={`${styles.formatBtn} ${italicFont === 'italic' ? styles.formatBtnActive : ''}`} aria-label={t('Italic')} title={t('Italic')} onClick={() => { italicFont === 'italic' ? setItalicFont('') : setItalicFont('italic'); saveToStorage('newNoteItalicFont', italicFont === 'italic' ? '' : 'italic'); }}><MdFormatItalic size={17} /></button>
                <div className={styles.fmtDivider} />
                {['A', 'B', 'C', 'D', 'E'].map((sz) => (
                  <button key={sz} type="button" className={`${styles.sizeBtn} ${styles['estiloTexto' + sz]} ${font === 'estiloTexto' + sz ? styles.sizeBtnActive : ''}`} aria-label={sz} title={sz} onClick={() => { setFont('estiloTexto' + sz); saveToStorage('newNoteFont', 'estiloTexto' + sz); }}>{sz}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tutorial Premium */}
        {showTutorial && (
          <div className={`${styles.tutorialOverlay} ${styles.visible}`}>
            <div className={styles.tutorialContainer}>
              <button type="button" className={styles.tutorialClose} onClick={() => setShowTutorial(false)}>
                <MdClose size={20} />
              </button>

              <div className={styles.tutorialCarousel}>
                <div className={styles.tutorialContent} style={{ transform: `translateX(-${tutorialStep * 100}%)` }}>

                  {/* Slide 1: Editor & Emojis */}
                  <div className={styles.tutorialSlide}>
                    <div className={styles.tutorialIconWrapper}><BsStars size={32} /></div>
                    <h3 className={styles.tutorialTitle}>{t('Express your ideas')}</h3>
                    <p className={styles.tutorialText}>{t('Create amazing notes with smart emoji support and a generous text limit. Type and express yourself freely!')}</p>
                  </div>

                  {/* Slide 2: Interactive Lists */}
                  <div className={styles.tutorialSlide}>
                    <div className={styles.tutorialIconWrapper}><BsListUl size={32} /></div>
                    <h3 className={styles.tutorialTitle}>{t('Interactive Lists')}</h3>
                    <p className={styles.tutorialText}>{t('Add items, mark them as done, and drag to reorder. Stay organized your way.')}</p>
                  </div>

                  {/* Slide 3: Advanced Styles */}
                  <div className={styles.tutorialSlide}>
                    <div className={styles.tutorialIconWrapper}><MdPalette size={32} /></div>
                    <h3 className={styles.tutorialTitle}>{t('Unique Style')}</h3>
                    <p className={styles.tutorialText}>{t('Customize background colors, text colors, and fonts via the advanced settings panel.')}</p>
                  </div>

                  {/* Slide 4: Priority & Attachments */}
                  <div className={styles.tutorialSlide}>
                    <div className={styles.tutorialIconWrapper}><BsImage size={32} /></div>
                    <h3 className={styles.tutorialTitle}>{t('Priority & More')}</h3>
                    <p className={styles.tutorialText}>{t('Highlight what matters by marking notes as priority. And get ready for photos, PDFs, and audio soon!')}</p>
                  </div>

                </div>
              </div>

              <div className={styles.tutorialFooter}>
                <div className={styles.tutorialDots}>
                  {[0, 1, 2, 3].map(step => (
                    <span key={step} className={`${styles.tutorialDot} ${tutorialStep === step ? styles.tutorialDotActive : ''}`} />
                  ))}
                </div>
                <div className={styles.tutorialActions}>
                  {tutorialStep > 0 && (
                    <button type="button" className={styles.tutorialBtnNav} onClick={() => setTutorialStep(prev => prev - 1)}>
                      {t('Back')}
                    </button>
                  )}
                  {tutorialStep < 3 ? (
                    <button type="button" className={`${styles.tutorialBtnNav} ${styles.tutorialBtnPrimary}`} onClick={() => setTutorialStep(prev => prev + 1)}>
                      {t('Next')}
                    </button>
                  ) : (
                    <button type="button" className={`${styles.tutorialBtnNav} ${styles.tutorialBtnPrimary}`} onClick={() => setShowTutorial(false)}>
                      {t('Got it!')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </form>
    </div>
  );
};

export default NewNote;